import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, X, ChevronDown, ShoppingCart, Plus, Check, Copy, ExternalLink, CheckCircle2, Clock, Loader2, Gift, CreditCard, QrCode, ArrowLeft, Ticket, Users, Bell, BellRing } from "lucide-react";
import { api, formatError, money, fmtDateTime, resolveFileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Screen } from "../components/ui-kit";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import ImageDropzone from "../components/ImageDropzone";
import QRCode from "react-qr-code";

const SalesCountdown = ({ opensAt, note }) => {
  const [, tick] = React.useState(0);
  React.useEffect(() => { const t = setInterval(() => tick((n) => n + 1), 30000); return () => clearInterval(t); }, []);
  if (!opensAt) return <>{note}</>;
  let diff = Math.max(0, new Date(opensAt).getTime() - Date.now());
  const d = Math.floor(diff / 86400000); diff -= d * 86400000;
  const h = Math.floor(diff / 3600000); diff -= h * 3600000;
  const m = Math.floor(diff / 60000);
  const rel = d > 0 ? `${d}d ${h}h` : (h > 0 ? `${h}h ${m}m` : `${m}m`);
  const wd = new Date(opensAt).toLocaleString("en-SG", { weekday: "long", timeZone: "Asia/Singapore" });
  const tm = new Date(opensAt).toLocaleString("en-SG", { hour: "numeric", minute: "2-digit", hour12: true, timeZone: "Asia/Singapore" }).replace(":00", "").replace(/\s/g, "").toLowerCase();
  return <>Sales open {wd} {tm} — in {rel}</>;
};

export default function Packages() {
  const { checkAuth, user } = useAuth();
  const navigate = useNavigate();
  const [gate, setGate] = useState(null);
  const [packages, setPackages] = useState([]);
  const [groups, setGroups] = useState([]);
  const [friends, setFriends] = useState([]);
  const [cart, setCart] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [opt, setOpt] = useState({});
  const [codeInput, setCodeInput] = useState("");
  const [promo, setPromo] = useState(null);
  const [activeTab, setActiveTab] = useState(() => {
    const g = new URLSearchParams(window.location.search).get("group");
    return g || "all";
  });
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [prereqPrompt, setPrereqPrompt] = useState(null);
  const [busy, setBusy] = useState(false);
  // Phase B checkout flow
  const [step, setStep] = useState("cart"); // cart | pay | done
  const [order, setOrder] = useState(null);
  const [methods, setMethods] = useState([]);
  const [method, setMethod] = useState(null);
  const [proofUrl, setProofUrl] = useState("");
  const [payMode, setPayMode] = useState(null);
  const [socials, setSocials] = useState([]);
  const [socialSel, setSocialSel] = useState({});
  const [classes, setClasses] = useState([]);
  const [classSel, setClassSel] = useState({});
  const [owned, setOwned] = useState([]);
  const [searchParams] = useSearchParams();

  const isNotPast = (iso) => { if (!iso) return true; const p = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Singapore", year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date(iso)); const g = (t) => p.find((x) => x.type === t).value; return new Date(`${g("year")}-${g("month")}-${g("day")}T23:59:59+08:00`).getTime() >= Date.now(); };
  const loadPackages = (code) => api.get(`/packages${code ? `?code=${encodeURIComponent(code)}` : ""}`).then((r) => setPackages(r.data)).catch(() => {});
  const toggleNotify = async (p) => {
    try {
      if (p.notify_watching) { await api.delete(`/packages/${p.id}/notify-me`); toast.success("You'll no longer be notified"); }
      else { await api.post(`/packages/${p.id}/notify-me`); toast.success("We'll notify you when sales open"); }
      setPackages((prev) => prev.map((x) => (x.id === p.id ? { ...x, notify_watching: !p.notify_watching } : x)));
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  useEffect(() => {
    const pkgId = new URLSearchParams(window.location.search).get("pkg");
    if (pkgId && packages.length) {
      const p = packages.find((x) => x.id === pkgId);
      if (p) { setActiveTab(p.group || "all"); setExpanded((e) => ({ ...e, [p.id]: true })); }
    }
  }, [packages]);
  useEffect(() => {
    const codeParam = (new URLSearchParams(window.location.search).get("code") || "").trim();
    if (codeParam) {
      api.get(`/promo/validate?code=${encodeURIComponent(codeParam)}`).then((r) => {
        if (!r.data.valid) { toast.error("Invalid or expired code"); loadPackages(); return; }
        setPromo(r.data); setCodeInput(codeParam.toUpperCase());
        api.post("/promo/visit", { code: codeParam }).catch(() => {});
        if (r.data.kind === "special") { loadPackages(codeParam); setActiveTab("unlocked"); toast.success("Hidden packages unlocked"); }
        else { loadPackages(); toast.success(`${r.data.discount_percent}% discount applied`); }
      }).catch(() => { loadPackages(); });
    } else {
      loadPackages();
    }
    api.get("/pkg-groups").then((r) => setGroups(r.data)).catch(() => {});
    api.get("/friends").then((r) => setFriends(r.data.friends || [])).catch(() => {});
    api.get("/payment-methods").then((r) => setMethods(r.data)).catch(() => {});
    api.get("/events?category=socials&upcoming=1").then((r) => setSocials((r.data || []).filter((s) => isNotPast(s.starts_at)))).catch(() => {});
    api.get("/classes").then((r) => setClasses((r.data || []).filter((c) => isNotPast(c.starts_at)))).catch(() => {});
    api.get("/passes").then((r) => setOwned(r.data || [])).catch(() => {});
  }, []);

  // Deep link from the calendar / dashboard: ?group=socials jumps straight to that ticket tab.
  useEffect(() => {
    const g = searchParams.get("group");
    if (g && !searchParams.get("code")) { setActiveTab(g); window.scrollTo({ top: 0, behavior: "smooth" }); }
  }, [searchParams]);

  const applyCode = async () => {
    const code = codeInput.trim();
    if (!code) return;
    try {
      const r = await api.get(`/promo/validate?code=${encodeURIComponent(code)}`);
      if (!r.data.valid) { toast.error("Invalid or expired code"); return; }
      setPromo(r.data);
      if (r.data.kind === "special") { await loadPackages(code); setActiveTab("unlocked"); window.scrollTo({ top: 0, behavior: "smooth" }); toast.success("Hidden packages unlocked — showing your tickets"); }
      else toast.success(`${r.data.discount_percent}% discount applied`);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const clearCode = () => { setPromo(null); setCodeInput(""); loadPackages(); };

  const discount = promo?.kind === "promotion" ? promo.discount_percent : 0;
  const basePrice = (p) => p.current_price ?? p.price;
  const contrastText = (hex) => {
    const h = String(hex || "").replace("#", "");
    if (h.length < 6) return "#000";
    const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) > 150 ? "#000" : "#fff";
  };
  const isQuarterly = (p) => (p.quarter_options || []).length > 0;
  const selQuarterKey = (p) => opt[p.id] || p.quarter_options?.[0]?.key;
  const selQuarter = (p) => (p.quarter_options || []).find((q) => q.key === selQuarterKey(p)) || p.quarter_options?.[0];
  const quarterPrice = (p) => { const q = selQuarter(p); return Math.round((Number(q?.weeks) || 0) * (Number(p.price_per_week) || 0) * 100) / 100; };
  const priceOf = (p) => (isQuarterly(p) ? quarterPrice(p) : (discount ? Math.round(basePrice(p) * (1 - discount / 100) * 100) / 100 : basePrice(p)));

  const addToCart = (p) => {
    if (isQuarterly(p)) {
      const q = selQuarter(p);
      if (!q) return;
      setCart((c) => [...c, { key: `${p.id}-${Date.now()}`, package_id: p.id, name: p.name, price: quarterPrice(p), group: p.group, option: q.key, optionLabel: q.label, needsFriend: false, friend_id: "", color: p.color, group_size: p.group_size || 0, group_member_ids: [], social_event_id: "", social_title: "", social_date: "" }]);
      toast.success(`${p.name} · ${q.label} added`);
      return;
    }
    const needsOption = (p.options || []).length > 0;
    const option = needsOption ? (opt[p.id] || p.options[0]) : null;
    const soc = p.group === "socials" && socials.length > 0 && (p.credits || 0) <= 1
      ? (socials.find((s) => s.id === socialSel[p.id]) || [...socials].sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at))[0])
      : null;
    setCart((c) => [...c, {
      key: `${p.id}-${Date.now()}`, package_id: p.id, name: p.name, price: priceOf(p),
      group: p.group, option, needsFriend: !!p.requires_friend && (p.group_size || 0) <= 1, friend_id: "", color: p.color,
      group_size: p.group_size || 0, group_member_ids: [],
      social_event_id: soc?.id || "", social_title: soc?.title || "", social_date: soc?.starts_at || "",
      class_bookings: p.group === "classes" ? (classSel[p.id] || []) : [],
      anyDate: (p.credits || 0) > 1,
    }]);
    toast.success(soc ? `${p.name} · ${fmtDateTime(soc.starts_at)} added` : `${p.name} added`);
  };
  const addRequired = (rp) => {
    const found = packages.find((x) => x.id === rp.id);
    if (found) { addToCart(found); }
    else {
      setCart((c) => [...c, {
        key: `${rp.id}-${Date.now()}`, package_id: rp.id, name: rp.name, price: rp.price,
        group: rp.group, option: (rp.options && rp.options.length) ? rp.options[0] : null,
        needsFriend: !!rp.requires_friend && (rp.group_size || 0) <= 1, friend_id: "", color: "#B026FF",
        group_size: rp.group_size || 0, group_member_ids: [], social_event_id: "", social_title: "", social_date: "", class_bookings: [],
      }]);
      toast.success(`${rp.name} added`);
    }
    setPrereqPrompt(null);
  };
  const removeItem = (key) => setCart((c) => c.filter((x) => x.key !== key));
  const setFriend = (key, fid) => setCart((c) => c.map((x) => (x.key === key ? { ...x, friend_id: fid } : x)));
  const setGroupMember = (key, idx, fid) => setCart((c) => c.map((x) => { if (x.key !== key) return x; const arr = [...(x.group_member_ids || [])]; arr[idx] = fid; return { ...x, group_member_ids: arr }; }));
  const setSocial = (key, ev) => setCart((c) => c.map((x) => (x.key === key ? { ...x, social_event_id: ev?.id || "", social_title: ev?.title || "", social_date: ev?.starts_at || "" } : x)));
  const classOptions = (p) => (classes || []).filter((c) => (p.class_event_ids || []).includes(c.id)).sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at));
  const toggleClassSel = (pid, cid) => setClassSel((s) => { const cur = s[pid] || []; return { ...s, [pid]: cur.includes(cid) ? cur.filter((x) => x !== cid) : [...cur, cid] }; });
  const cartTotal = cart.reduce((s, x) => s + x.price, 0);
  const isWkMaster = (x) => x.group === "weekender" && (x.name || "").toLowerCase().includes("master class");
  const isWkFull = (x) => x.group === "weekender" && (x.group_size || 0) <= 1 && (((x.name || "").toLowerCase().includes("full")) || ((x.name || "").toLowerCase().includes("student pass")));
  const hasFullPass = cart.some(isWkFull) || owned.some((p) => p.group === "weekender" && (((p.package_name || "").toLowerCase().includes("full")) || ((p.package_name || "").toLowerCase().includes("student pass"))));
  const masterBlocked = cart.some(isWkMaster) && !hasFullPass;

  // Socials: auto-select the nearest upcoming social night for any socials item without one (users can still pick another).
  useEffect(() => {
    if (socials.length === 0) return;
    const nearest = [...socials].sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at))[0];
    setCart((c) => {
      let changed = false;
      const next = c.map((x) => {
        if (x.group === "socials" && !x.anyDate && !x.social_event_id) { changed = true; return { ...x, social_event_id: nearest.id, social_title: nearest.title, social_date: nearest.starts_at }; }
        return x;
      });
      return changed ? next : c;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socials, cart.length]);

  // Re-price cart items when a promotion discount is applied/cleared so the total stays correct without re-adding.
  useEffect(() => {
    setCart((c) => {
      let changed = false;
      const next = c.map((x) => {
        if (x.optionLabel) return x; // quarterly items keep their picked price
        const pkg = packages.find((pp) => pp.id === x.package_id);
        if (!pkg) return x;
        const np = priceOf(pkg);
        if (np !== x.price) { changed = true; return { ...x, price: np }; }
        return x;
      });
      return changed ? next : c;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discount]);

  const [hpBusy, setHpBusy] = useState(false);
  const [hpPolling, setHpPolling] = useState(false);
  const [hpStatus, setHpStatus] = useState("idle");
  const [hpQr, setHpQr] = useState("");
  const [hpView, setHpView] = useState("checkout");
  const pollRef = useRef(null);
  const winRef = useRef(null);
  const watchRef = useRef(null);

  const clearTimers = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (watchRef.current) { clearInterval(watchRef.current); watchRef.current = null; }
    setHpPolling(false);
  };
  useEffect(() => { if (!checkoutOpen) { clearTimers(); setPayMode(null); } }, [checkoutOpen]);

  const openCheckout = () => { setStep("cart"); setOrder(null); setProofUrl(""); setHpStatus("idle"); setHpQr(""); setMethod(methods[0] || null); setCheckoutOpen(true); };

  useEffect(() => {
    const payId = searchParams.get("pay");
    if (payId) {
      api.get(`/orders/${payId}`).then((r) => {
        if (r.data.status !== "paid") { setOrder(r.data); setStep("pay"); setHpStatus("idle"); setHpQr(""); setCheckoutOpen(true); }
        else toast.message("This order is already paid.");
      }).catch(() => {});
    }
    // eslint-disable-next-line
  }, [searchParams]);

  // Step 1 → create the order (no passes granted yet)
  const proceed = async () => {
    for (const it of cart) {
      if (it.needsFriend && (it.group_size || 0) <= 1 && !it.friend_id) { toast.error(`Pick a friend for ${it.name}`); return; }
      if (it.group_size > 1) {
        const ids = (it.group_member_ids || []).filter(Boolean);
        const need = it.group_size - 1;
        if (ids.length !== need) { toast.error(`Select ${need} friends for ${it.name}`); return; }
        if (new Set(ids).size !== ids.length) { toast.error(`Each friend for ${it.name} must be different`); return; }
      }
      if (it.group === "socials" && !it.anyDate && socials.length > 0 && !it.social_event_id) { toast.error(`Pick a social night for ${it.name}`); return; }
    }
    setBusy(true);
    try {
      const items = cart.map((x) => ({ package_id: x.package_id, option: x.option, friend_id: x.needsFriend ? x.friend_id : undefined, group_member_ids: x.group_size > 1 ? (x.group_member_ids || []).filter(Boolean) : undefined, social_event_id: x.group === "socials" && !x.anyDate ? (x.social_event_id || undefined) : undefined, class_bookings: x.group === "classes" ? (x.class_bookings || []) : undefined }));
      const r = await api.post("/orders", { items, code: promo?.code || undefined });
      setOrder(r.data);
      setStep("pay");
    } catch (e) {
      const d = e.response?.data?.detail;
      const code = d && typeof d === "object" ? d.code : "";
      if (code === "prereq_required") { setPrereqPrompt(d); }
      else {
        toast.error(formatError(d));
        if (code === "email_unverified") { setCheckoutOpen(false); setGate("email"); }
        else if (code === "gender_required") { setCheckoutOpen(false); setGate("gender"); }
      }
    }
    finally { setBusy(false); }
  };

  // Step 2 → submit proof for admin verification
  const submitProof = async () => {
    if (!method) { toast.error("Choose a payment method"); return; }
    if (!proofUrl) { toast.error("Upload your payment screenshot first"); return; }
    setBusy(true);
    try {
      await api.post(`/orders/${order.id}/proof`, { proof_url: proofUrl, method: method.label });
      setStep("done");
      setCart([]);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const checkStatus = async () => {
    try {
      const r = await api.get(`/orders/${order.id}`);
      if (r.data.status === "paid") {
        clearTimers();
        setHpStatus("paid"); setHpQr("");
        setStep("done"); setCart([]);
        toast.success("Payment confirmed — your passes are active!");
        return true;
      }
    } catch (e) { console.error("Poll order failed", e); }
    return false;
  };

  const checkNow = async () => {
    const ok = await checkStatus();
    if (!ok) toast.message("No payment detected yet — finish paying, then tap again.");
  };

  const startPoll = () => {
    setHpPolling(true);
    let tries = 0;
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      tries += 1;
      const paid = await checkStatus();
      if (!paid && tries >= 60) { clearTimers(); setHpStatus("timeout"); }
    }, 3000);
  };

  const watchWindow = () => {
    if (watchRef.current) clearInterval(watchRef.current);
    watchRef.current = setInterval(() => {
      if (winRef.current && winRef.current.closed) {
        clearInterval(watchRef.current); watchRef.current = null;
        setTimeout(async () => {
          const paid = await checkStatus();
          if (!paid) { clearTimers(); setHpStatus("cancelled"); }
        }, 1500);
      }
    }, 800);
  };

  const startPayment = async (view) => {
    setHpBusy(true); setHpQr(""); setHpView(view); setHpStatus("idle");
    try {
      const r = await api.post(`/orders/${order.id}/hitpay`, { view });
      if (view === "paynow_qr" && r.data.qr_code) {
        setHpQr(r.data.qr_code); setHpStatus("awaiting"); startPoll();
      } else if (r.data.checkout_url) {
        winRef.current = window.open(r.data.checkout_url, "_blank");
        setHpStatus("awaiting"); startPoll(); watchWindow();
      } else {
        toast.error("Could not start online payment. Please use the manual option below.");
      }
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setHpBusy(false); }
  };

  const copyRef = async () => {
    try { await navigator.clipboard.writeText(order.reference); toast.success("Reference copied"); }
    catch { toast.message(`Reference: ${order.reference}`); }
  };

  const backToModes = () => { clearTimers(); setHpStatus("idle"); setHpQr(""); setPayMode(null); };

  const fallbackKey = groups[0]?.key || "classes";
  const matchesTab = (p) => {
    if (activeTab === "all") return true;
    if (activeTab === "unlocked") return !!p.unlock_code;
    return (p.group || fallbackKey) === activeTab;
  };
  const hasUnlocked = packages.some((p) => p.unlock_code);
  const groupsWithPkgs = groups.filter((g) => packages.some((p) => (p.group || fallbackKey) === g.key));
  const tabs = [{ key: "all", label: "All" }, ...groupsWithPkgs.map((g) => ({ key: g.key, label: g.label })), ...(hasUnlocked ? [{ key: "unlocked", label: "Unlocked" }] : [])];
  const visibleGroups = groups.filter((g) => packages.some((p) => (p.group || fallbackKey) === g.key && matchesTab(p)));

  return (
    <Screen>
      <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-2 mb-4 mt-2">
        <KeyRound size={16} className="text-[#D4AF37] shrink-0" />
        <input value={codeInput} onChange={(e) => setCodeInput(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === "Enter" && applyCode()} data-testid="promo-code-input" placeholder="Enter special / promo code" className="flex-1 min-w-0 bg-transparent outline-none text-sm placeholder:text-white/30" />
        {promo ? <button onClick={clearCode} data-testid="promo-clear" className="text-[#A19DB0] shrink-0"><X size={16} /></button>
               : <button onClick={applyCode} data-testid="promo-apply" className="text-[#D4AF37] text-sm font-semibold shrink-0">Apply</button>}
      </div>
      {promo && <div data-testid="promo-active" className="text-xs text-[#D4AF37] -mt-3 mb-5">{promo.kind === "special" ? `Code ${promo.code} — hidden packages unlocked` : `Code ${promo.code} — ${promo.discount_percent}% off applied`}</div>}

      <div className="flex flex-nowrap gap-2 mb-4 overflow-x-auto no-scrollbar" data-testid="pkg-tabs">
        {tabs.map((t) => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} data-testid={`tab-${t.key}`} className={`shrink-0 whitespace-nowrap rounded-full border px-3.5 py-1.5 text-sm transition-colors ${activeTab === t.key ? "bg-[#D4AF37] text-black border-[#D4AF37] font-semibold" : "border-white/10 bg-white/5 text-[#E8E4F0] hover:border-[#D4AF37]/50"}`}>{t.label}</button>
        ))}
      </div>

      <div className="space-y-5 pb-28">
        {visibleGroups.map((g) => {
          const groupPkgs = packages.filter((p) => (p.group || fallbackKey) === g.key && matchesTab(p));
          if (groupPkgs.length === 0) return null;
          return (
            <section key={g.key} id={`group-${g.key}`} data-testid={`customer-group-${g.key}`} className="scroll-mt-4">
              <div className="mb-2"><h2 className="font-display text-lg font-semibold text-[#D4AF37]">{g.label}</h2>{g.blurb && <p className="text-[#A19DB0] text-xs mt-0.5">{g.blurb}</p>}</div>
              <div className="space-y-3">
                {groupPkgs.map((p) => {
                  const price = priceOf(p);
                  const isVipPriced = ((user?.role === "vip") || (user?.role === "student" && p.group === "socials")) && p.vip_price != null && p.vip_price !== "" && Number(p.current_price) < Number(p.price);
                  const willChange = p.price_after != null && p.price_change_at && new Date(p.price_change_at) > new Date();
                  const hasIncl = (p.inclusions || []).length > 0;
                  const nameParts = (p.name || "").trim().split(" ");
                  const nameHead = nameParts.length > 1 ? nameParts.slice(0, -1).join(" ") : p.name;
                  const nameTail = nameParts.length > 1 ? nameParts[nameParts.length - 1] : "";
                  const isBeginner = p.group === "classes" && String(p.class_level || "").toLowerCase().includes("begin");
                  const overline = (p.group === "classes" && p.class_level) ? `${p.class_level} Class` : g.label;
                  const chips = [];
                  if (p.credits > 0) chips.push({ Icon: Ticket, text: p.group === "classes" ? `${p.credits} class pass${p.credits !== 1 ? "es" : ""}` : `${p.credits} pass${p.credits !== 1 ? "es" : ""}` });
                  if (isBeginner) chips.push({ Icon: Gift, text: "1 free social" });
                  if (p.group === "socials" && chips.length === 0) chips.push({ Icon: Ticket, text: `${p.credits || 1} Socials credit${(p.credits || 1) !== 1 ? "s" : ""}` });
                  if ((p.group_size || 0) > 1) chips.push({ Icon: Users, text: `Group of ${p.group_size}` });
                  else if (p.requires_friend) chips.push({ Icon: Users, text: "You + a friend" });
                  const priceSub = isQuarterly(p) ? "Per term" : "One-time payment";
                  const rateInfo = p.sublabel_name
                    ? { text: p.sublabel_name, color: p.sublabel_color || "#B026FF" }
                    : (p.unlock_code ? (() => {
                      const uc = String(p.unlock_code).toUpperCase();
                      if (uc.includes("AFFSW")) return { text: "Special rate", color: "#FFC300" };
                      if (uc.includes("NVFSW") || uc.includes("DMIFSW")) return { text: "Performance rate", color: "#B026FF" };
                      if (uc.includes("FGFSW")) return { text: "Student rate", color: "#5CC8FF" };
                      return { text: "Unlocked rate", color: "#FFC300" };
                    })() : null);
                  const wkDays = p.group === "weekender" ? Math.ceil((new Date("2026-11-20T00:00:00+08:00") - new Date()) / 86400000) : 0;
                  return (
                    <div key={p.id} data-testid={`package-${p.id}`} className="rounded-3xl border border-white/10 bg-[#0B0B0F] overflow-hidden relative">
                      <span className="absolute left-0 top-0 bottom-0 w-1.5 z-10 rounded-r-full" style={{ background: p.color || "#B026FF" }} />
                      {p.image && <img src={p.image} alt={p.name} className="w-full h-24 object-cover" />}
                      <div className="p-5 pl-6">
                        {(rateInfo || wkDays > 0) && (
                          <div className="flex items-center flex-wrap gap-2 mb-3">
                            {rateInfo && <span data-testid={`rate-badge-${p.id}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-bold rounded-full px-2.5 py-1" style={{ background: rateInfo.color, color: contrastText(rateInfo.color) }}>{rateInfo.text}</span>}
                            {wkDays > 0 && <span data-testid={`countdown-${p.id}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-semibold rounded-full px-2.5 py-1 bg-[#FFC300]/[0.12] text-[#FFC300] border border-[#FFC300]/30"><Clock size={12} /> Nov 20–21 · {wkDays} {wkDays === 1 ? "day" : "days"} to go</span>}
                          </div>
                        )}
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-[11px] uppercase tracking-[0.18em] font-semibold" style={{ color: "#B026FF" }}>{overline}</p>
                            <h3 className="font-display text-2xl font-bold mt-1 leading-tight">
                              <span className="text-white">{nameHead}</span>{nameTail ? <> <span className="text-[#FFC300]">{nameTail}</span></> : null}
                            </h3>
                          </div>
                          <div className="text-right shrink-0">
                            {discount > 0 && basePrice(p) > 0 && <p className="text-[#A19DB0] line-through text-sm">{money(basePrice(p))}</p>}
                            {discount === 0 && isVipPriced && <p data-testid={`vip-original-${p.id}`} className="text-[#A19DB0] line-through text-sm">{money(p.price)}</p>}
                            <p className="font-display text-3xl font-bold text-[#FFC300] leading-none" style={{ fontVariantNumeric: "lining-nums" }}>{price === 0 ? "Free" : money(price)}</p>
                            <p className="text-[11px] mt-1.5"><span className="uppercase tracking-wide font-semibold" style={{ color: "#B026FF" }}>Price</span> <span className="text-[#A19DB0]">· {priceSub}</span></p>
                            {isVipPriced && <span data-testid={`vip-save-${p.id}`} className={`mt-1.5 inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-bold rounded-full px-2 py-0.5 ${user?.role === "vip" ? "text-white bg-[#EC4899]" : "text-black bg-[#FFC300]"}`}>{user?.role === "vip" ? "VIP" : "Student"} · save {money(Number(p.price) - Number(p.current_price))}</span>}
                          </div>
                        </div>
                        {chips.length > 0 && (
                          <div className="flex items-center flex-wrap gap-x-3 gap-y-2 mt-4" data-testid={`chips-${p.id}`}>
                            {chips.map((c, i) => (
                              <React.Fragment key={i}>
                                {i > 0 && <span className="w-px h-4 bg-white/15" />}
                                <span className="flex items-center gap-2 text-sm text-[#E8E4F0]"><c.Icon size={16} className="text-[#FFC300]" /> {c.text}</span>
                              </React.Fragment>
                            ))}
                          </div>
                        )}
                        {(p.requires_names || []).length > 0 && (
                          <div data-testid={`requires-badge-${p.id}`} className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-medium text-[#E5A84B] bg-[#E5A84B]/10 border border-[#E5A84B]/30 rounded-full px-2.5 py-1">
                            <Ticket size={12} /> Requires {p.requires_names.join(" or ")} first
                          </div>
                        )}
                        {(p.group_size || 0) > 1 && (() => {
                          const per = Math.round((price / p.group_size) * 100) / 100;
                          const save = p.single_price ? Math.round((p.single_price * p.group_size - price) * 100) / 100 : 0;
                          return (
                            <div data-testid={`group-savings-${p.id}`} className="mt-3 flex items-center gap-2 rounded-xl bg-[#FFC300]/[0.08] border border-[#FFC300]/25 px-3 py-2">
                              <Users size={15} className="text-[#FFC300] shrink-0" />
                              <span className="text-sm text-[#E8E4F0]"><span className="font-semibold text-[#FFC300]">{money(per)}</span> per person{save > 0 ? <> · <span className="font-semibold text-[#4ADE80]">save {money(save)}</span></> : null}</span>
                            </div>
                          );
                        })()}
                        {p.description && <p className="text-[#A19DB0] text-xs mt-3 whitespace-pre-line">{p.description}</p>}
                        {hasIncl && (
                          <button onClick={() => setExpanded((e) => ({ ...e, [p.id]: !e[p.id] }))} data-testid={`expand-${p.id}`} className="flex items-center gap-1 text-[#FFC300] text-sm mt-2">
                            What you get <ChevronDown size={15} className={`transition-transform ${expanded[p.id] ? "rotate-180" : ""}`} />
                          </button>
                        )}
                        {hasIncl && expanded[p.id] && (
                          <ul data-testid={`inclusions-${p.id}`} className="mt-2 space-y-1.5">
                            {p.inclusions.map((it, i) => <li key={`${p.id}-incl-${i}`} className="flex gap-2 text-sm text-[#E8E4F0]"><Check size={15} className="text-[#FFC300] mt-0.5 shrink-0" /> {it}</li>)}
                          </ul>
                        )}
                        {(p.options || []).length > 0 && (
                          <select value={opt[p.id] || p.options[0]} onChange={(e) => setOpt((o) => ({ ...o, [p.id]: e.target.value }))} data-testid={`option-${p.id}`} className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 mt-3 text-sm outline-none focus:border-[#FFC300]">
                            {p.options.map((o) => <option key={o} value={o} className="bg-[#140F1E]">{o}</option>)}
                          </select>
                        )}
                        {isQuarterly(p) && (
                          <div className="mt-3">
                            <select value={selQuarterKey(p)} onChange={(e) => setOpt((o) => ({ ...o, [p.id]: e.target.value }))} data-testid={`quarter-${p.id}`} className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#FFC300]">
                              {p.quarter_options.map((q) => <option key={q.key} value={q.key} className="bg-[#140F1E]">{q.label} — {money((Number(q.weeks) || 0) * (Number(p.price_per_week) || 0))} ({q.weeks} wks)</option>)}
                            </select>
                            <p className="text-[11px] text-[#A19DB0] mt-1" data-testid={`quarter-hint-${p.id}`}>{money(p.price_per_week || 0)}/week × {selQuarter(p)?.weeks || 0} weeks · {selQuarter(p)?.weeks || 0} Focus class passes this quarter</p>
                          </div>
                        )}
                        {p.group === "socials" && (p.credits || 0) > 1 && <p className="mt-3 text-[11px] text-[#A19DB0]" data-testid={`social-anydate-${p.id}`}>Use each pass at any social night</p>}
                        {p.group === "socials" && (p.credits || 0) <= 1 && socials.length > 0 && (
                          <div className="mt-3" data-testid={`social-date-block-${p.id}`}>
                            <label className="text-[11px] text-[#A19DB0]">Pick your social night</label>
                            <select value={socialSel[p.id] || ([...socials].sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at))[0]?.id || "")} onChange={(e) => setSocialSel((s) => ({ ...s, [p.id]: e.target.value }))} data-testid={`social-date-${p.id}`} className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 mt-1 text-sm outline-none focus:border-[#FFC300]">
                              {[...socials].sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at)).map((s) => <option key={s.id} value={s.id} className="bg-[#140F1E]">{s.title} · {fmtDateTime(s.starts_at)}</option>)}
                            </select>
                          </div>
                        )}
                        {p.group === "classes" && (p.class_event_ids || []).length > 0 && classOptions(p).length > 0 && (
                          <div className="mt-3" data-testid={`class-date-block-${p.id}`}>
                            <label className="text-[11px] text-[#A19DB0]">Pick your class date(s) — we'll book them for you</label>
                            <div className="mt-1.5 space-y-1.5 max-h-44 overflow-y-auto pr-1">
                              {classOptions(p).map((c) => {
                                const sel = (classSel[p.id] || []).includes(c.id);
                                return (
                                  <label key={c.id} data-testid={`class-date-${p.id}-${c.id}`} className={`flex items-center gap-2 rounded-xl border px-3 py-2 cursor-pointer text-sm transition-colors ${sel ? "border-[#FFC300]/60 bg-[#FFC300]/[0.06]" : "border-white/10 bg-white/5 hover:bg-white/[0.08]"}`}>
                                    <input type="checkbox" checked={sel} onChange={() => toggleClassSel(p.id, c.id)} className="accent-[#FFC300] shrink-0" />
                                    <span className="min-w-0 flex-1"><span className="block truncate">{c.title}</span><span className="block text-[11px] text-[#A19DB0]">{fmtDateTime(c.starts_at)}</span></span>
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        {willChange && <p className="text-[#A19DB0]/70 text-xs mt-2">Price rises to {money(p.price_after)} after {new Date(p.price_change_at).toLocaleDateString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Singapore" })} (SGT)</p>}
                        {p.remaining != null && p.remaining <= 5 && <p data-testid={`remaining-${p.id}`} className={`text-xs mt-1 font-semibold ${p.remaining === 0 ? "text-[#E57373]" : "text-[#FFC300]"}`}>{p.remaining === 0 ? "Sold out" : `Almost gone — only ${p.remaining} left`}</p>}
                        {p.level_locked && <p data-testid={`lock-reason-${p.id}`} className="mt-3 text-xs font-medium text-[#E5A84B] bg-[#E5A84B]/10 border border-[#E5A84B]/30 rounded-xl px-3 py-2">{p.lock_reason}</p>}
                        {p.sales_closed && <p data-testid={`sales-closed-${p.id}`} className="mt-3 text-xs font-medium text-[#E5A84B] bg-[#E5A84B]/10 border border-[#E5A84B]/30 rounded-xl px-3 py-2"><SalesCountdown opensAt={p.sales_opens_at} note={p.sales_note} /></p>}
                        {p.sales_closed ? (
                          <button onClick={() => toggleNotify(p)} data-testid={`notify-me-${p.id}`} className={`w-full mt-5 rounded-full py-3.5 font-semibold flex items-center justify-center gap-2 border active:scale-[0.98] transition-all ${p.notify_watching ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10" : "border-[#FFC300]/60 text-[#FFC300] bg-transparent hover:bg-[#FFC300]/10"}`}>
                            {p.notify_watching ? <><BellRing size={18} /> We'll notify you</> : <><Bell size={18} /> Notify me when sales open</>}
                          </button>
                        ) : (
                          <button onClick={() => addToCart(p)} disabled={p.remaining === 0 || p.level_locked} data-testid={`add-${p.id}`} className="w-full mt-5 rounded-full py-3.5 font-semibold flex items-center justify-center gap-2 border border-[#FFC300]/60 text-[#FFC300] bg-transparent hover:bg-[#FFC300]/10 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                            <Plus size={18} /> {p.level_locked ? "Locked" : (p.remaining === 0 ? "Sold out" : "Add to cart")}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>

      {cart.length > 0 && (
        <div className="fixed bottom-[calc(env(safe-area-inset-bottom)+5rem)] left-1/2 -translate-x-1/2 w-[min(28rem,calc(100%-2rem))] z-40">
          <button onClick={openCheckout} data-testid="open-checkout" className="w-full flex items-center justify-between bg-[#D4AF37] text-black rounded-full px-5 py-3.5 font-semibold shadow-lg active:scale-[0.98] transition-transform">
            <span className="flex items-center gap-2"><ShoppingCart size={18} /> {cart.length} item{cart.length !== 1 ? "s" : ""}</span>
            <span>{money(cartTotal)} · Checkout</span>
          </button>
        </div>
      )}

      {gate && (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-6" data-testid="profile-gate">
          <div className="w-full max-w-sm rounded-3xl bg-[#1a1424] border border-white/10 p-6 text-center">
            <p className="font-display text-lg font-semibold">Complete your profile</p>
            <p className="text-[#A19DB0] text-sm mt-2">{gate === "email" ? "Please verify your email before you can buy a pass." : "Please tell us whether you are Male (M) or Female (F) before you can buy a pass."}</p>
            <button onClick={() => { const g = gate; setGate(null); navigate(`/profile?complete=${g}`); }} data-testid="profile-gate-go" className="mt-5 w-full rounded-full bg-[#D4AF37] text-black font-semibold py-3 active:scale-[0.98] transition-transform">Update my profile</button>
            <button onClick={() => setGate(null)} className="mt-2 w-full text-[#A19DB0] text-sm py-2">Not now</button>
          </div>
        </div>
      )}
      {prereqPrompt && (
        <div className="fixed inset-0 z-[110] bg-black/80 backdrop-blur-sm flex items-center justify-center p-6" data-testid="prereq-prompt">
          <div className="w-full max-w-sm rounded-3xl bg-[#1a1424] border border-white/10 p-6">
            <p className="font-display text-lg font-semibold text-center">One more ticket needed</p>
            <p className="text-[#A19DB0] text-sm mt-2 text-center">To buy <span className="text-[#D4AF37]">{prereqPrompt.for_package_name}</span> you first need {(prereqPrompt.required_package_names || []).join(" or ")}. Add it now:</p>
            <div className="mt-4 space-y-2">
              {(prereqPrompt.required_packages || []).map((rp) => (
                <button key={rp.id} data-testid={`prereq-add-${rp.id}`} onClick={() => addRequired(rp)} className="w-full flex items-center justify-between rounded-2xl border border-[#D4AF37]/50 bg-[#D4AF37]/[0.07] px-4 py-3 active:scale-[0.98] transition-transform">
                  <span className="font-medium text-[#E8E4F0]">{rp.name}</span>
                  <span className="flex items-center gap-1 text-[#D4AF37] font-semibold"><Plus size={16} /> {money(rp.price)}</span>
                </button>
              ))}
            </div>
            <button onClick={() => setPrereqPrompt(null)} className="mt-3 w-full text-[#A19DB0] text-sm py-2" data-testid="prereq-cancel">Not now</button>
          </div>
        </div>
      )}
      <Dialog open={checkoutOpen} onOpenChange={(o) => { setCheckoutOpen(o); if (!o && step === "done") navigate("/"); }}>
        <DialogContent className="bg-[#140F1E] border-white/10 text-white max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{step === "cart" ? "Review order" : step === "pay" ? "Choose payment" : "Payment submitted"}</DialogTitle>
            <DialogDescription className="sr-only">Checkout</DialogDescription>
          </DialogHeader>

          {step === "cart" && (
            <div className="space-y-3" data-testid="checkout-step-cart">
              {cart.map((it) => (
                <div key={it.key} data-testid={`cart-item-${it.package_id}`} className={`rounded-2xl border p-3 transition-opacity ${isWkMaster(it) && masterBlocked ? "bg-white/[0.02] border-white/5 opacity-45" : "bg-white/5 border-white/10"}`}>
                  <div className="flex items-center justify-between">
                    <div><p className="font-medium">{it.name}{it.optionLabel ? ` · ${it.optionLabel}` : it.option ? ` · ${it.option}` : ""}</p><p className="text-[#A19DB0] text-xs">{money(it.price)}</p></div>
                    <button onClick={() => removeItem(it.key)} data-testid={`remove-cart-${it.package_id}`} className="text-[#A19DB0]"><X size={16} /></button>
                  </div>
                  {isWkMaster(it) && masterBlocked && <p className="text-[11px] text-[#E5A84B] mt-2" data-testid={`cart-master-locked-${it.package_id}`}>🔒 Needs a Full Pass first</p>}
                  {it.group === "classes" && (it.class_bookings || []).length > 0 && (
                    <p className="text-[11px] text-[#A19DB0] mt-2" data-testid={`cart-classes-${it.package_id}`}>
                      Booking: {it.class_bookings.map((cid) => (classes.find((c) => c.id === cid)?.title) || "class").join(", ")}
                    </p>
                  )}
                  {it.group_size > 1 && (
                    <div className="mt-2 space-y-2" data-testid={`cart-group-${it.package_id}`}>
                      <p className="text-[11px] text-[#A19DB0]">Select {it.group_size - 1} friends — you and your {it.group_size - 1} friends each get a ticket</p>
                      {friends.length === 0 ? (
                        <p className="text-[#E57373] text-xs">Add friends first to use group buy.</p>
                      ) : (
                        Array.from({ length: it.group_size - 1 }).map((_, i) => (
                          <select key={i} value={(it.group_member_ids || [])[i] || ""} onChange={(e) => setGroupMember(it.key, i, e.target.value)} data-testid={`cart-group-${it.package_id}-${i}`} className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#D4AF37]">
                            <option value="" className="bg-[#140F1E]">— member {i + 1} —</option>
                            {friends.map((f) => <option key={f.id} value={f.id} className="bg-[#140F1E]">{f.name}</option>)}
                          </select>
                        ))
                      )}
                    </div>
                  )}
                  {it.needsFriend && (it.group_size || 0) <= 1 && (
                    friends.length === 0 ? (
                      <p className="text-[#E57373] text-xs mt-2">Add a friend first to buy this pass.</p>
                    ) : (
                      <select value={it.friend_id} onChange={(e) => setFriend(it.key, e.target.value)} data-testid={`cart-friend-${it.package_id}`} className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 mt-2 text-sm outline-none focus:border-[#D4AF37]">
                        <option value="" className="bg-[#140F1E]">— pick a friend —</option>
                        {friends.map((f) => <option key={f.id} value={f.id} className="bg-[#140F1E]">{f.name}</option>)}
                      </select>
                    )
                  )}
                  {it.group === "socials" && it.anyDate && <p className="text-[11px] text-[#A19DB0] mt-2" data-testid={`cart-anydate-${it.package_id}`}>Use each pass at any social night</p>}
                  {it.group === "socials" && !it.anyDate && socials.length > 0 && (
                    <>
                      <p className="text-[11px] text-[#A19DB0] mt-2">Social night · nearest date auto-selected — pick another upcoming date if you like</p>
                      <select value={it.social_event_id} onChange={(e) => setSocial(it.key, socials.find((s) => s.id === e.target.value))} data-testid={`cart-social-${it.package_id}`} className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 mt-1 text-sm outline-none focus:border-[#D4AF37]">
                        {[...socials].sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at)).map((s) => <option key={s.id} value={s.id} className="bg-[#140F1E]">{s.title} · {fmtDateTime(s.starts_at)}</option>)}
                      </select>
                    </>
                  )}
                </div>
              ))}
              <div className="flex items-center justify-between pt-2 border-t border-white/10">
                <span className="text-[#A19DB0]">Subtotal{discount ? ` (−${discount}%)` : ""}</span>
                <span className="font-display text-2xl font-bold text-[#D4AF37]" data-testid="checkout-total">{money(cartTotal)}</span>
              </div>
              {promo && (
                <p data-testid="checkout-code-applied" className="text-xs text-[#D4AF37] -mt-1">
                  {promo.kind === "special" ? `Code ${promo.code} applied — special rates unlocked` : `Code ${promo.code} applied — ${promo.discount_percent}% off`}
                </p>
              )}
              {masterBlocked && (
                <p data-testid="master-needs-full" className="mb-3 text-xs font-medium text-[#E5A84B] bg-[#E5A84B]/10 border border-[#E5A84B]/30 rounded-xl px-3 py-2">
                  The Master Class requires a Weekender Full Pass. Add a Full Pass to your cart (or buy one first) to continue.
                </p>
              )}
              <button onClick={proceed} disabled={busy || cart.length === 0 || masterBlocked} data-testid="proceed-to-pay" className="w-full rounded-full py-3.5 font-semibold bg-[#D4AF37] text-black active:scale-[0.98] transition-transform disabled:opacity-40 disabled:cursor-not-allowed">
                {busy ? "Creating order…" : "Proceed to payment"}
              </button>
            </div>
          )}

          {step === "pay" && order && (
            <div className="space-y-4" data-testid="checkout-step-pay">
              <div className="rounded-2xl bg-white/5 border border-white/10 p-4 space-y-1.5 text-sm">
                <Row label="Subtotal" value={money(order.subtotal)} />
                {order.fee > 0 && <Row label="Fee" value={money(order.fee)} />}
                {order.tax > 0 && <Row label="Tax" value={money(order.tax)} />}
                <div className="flex items-center justify-between pt-2 border-t border-white/10">
                  <span className="text-[#A19DB0]">Total to pay</span>
                  <span className="font-display text-2xl font-bold text-[#D4AF37]" data-testid="order-total">{money(order.total)}</span>
                </div>
              </div>

              {!payMode && (
                <div className="space-y-3" data-testid="pay-mode-chooser">
                  <p className="text-xs text-[#A19DB0]">How would you like to pay?</p>
                  {order.hitpay_enabled && (
                    <button onClick={() => { setPayMode("card"); startPayment("checkout"); }} data-testid="pay-mode-card" className="w-full text-left rounded-2xl border border-[#D4AF37]/40 bg-[#D4AF37]/[0.07] p-4 active:scale-[0.99] transition-transform">
                      <div className="flex items-center justify-between">
                        <p className="font-semibold text-[#E8E4F0] flex items-center gap-2"><CreditCard size={16} className="text-[#D4AF37]" /> Card</p>
                        <span className="text-[10px] font-semibold uppercase rounded-full px-2 py-0.5 bg-[#4ADE80]/15 text-[#4ADE80]">Instant</span>
                      </div>
                      <p className="text-xs text-[#A19DB0] mt-1.5">Your passes activate automatically once paid — no screenshot needed.</p>
                    </button>
                  )}
                  {order.payment_coming_soon && (
                    <div data-testid="pay-mode-coming-soon" className="w-full rounded-2xl border border-[#9D4EDD]/30 bg-[#9D4EDD]/[0.06] p-4">
                      <div className="flex items-center justify-between">
                        <p className="font-semibold text-[#E8E4F0] flex items-center gap-2"><CreditCard size={16} className="text-[#c79bff]" /> Card</p>
                        <span className="text-[10px] font-semibold uppercase rounded-full px-2 py-0.5 bg-[#9D4EDD]/20 text-[#c79bff]">Coming soon</span>
                      </div>
                      <p className="text-xs text-[#A19DB0] mt-1.5">Coming Soon — we're working on something exciting.</p>
                    </div>
                  )}
                  <button onClick={() => setPayMode("qr")} data-testid="pay-mode-qr" className="w-full text-left rounded-2xl border border-white/15 bg-white/5 p-4 active:scale-[0.99] transition-transform">
                    <div className="flex items-center justify-between">
                      <p className="font-semibold text-[#E8E4F0] flex items-center gap-2"><QrCode size={16} className="text-[#D4AF37]" /> QR code payment</p>
                      <span className="text-[10px] font-semibold uppercase rounded-full px-2 py-0.5 bg-[#E5A84B]/20 text-[#E5A84B]">Manual</span>
                    </div>
                    <p className="text-xs text-[#A19DB0] mt-1.5">PayNow / PayLah — your passes activate after validation. Add the reference code and upload a screenshot.</p>
                  </button>
                </div>
              )}

              {payMode === "card" && order.hitpay_enabled && (
                <div className="rounded-2xl border border-[#D4AF37]/40 bg-[#D4AF37]/[0.07] p-4 space-y-3" data-testid="hitpay-block">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm text-[#E8E4F0] font-medium">Pay by card — instant</p>
                    {hpStatus === "awaiting" && <span data-testid="hitpay-badge" className="inline-flex items-center gap-1.5 text-[11px] font-semibold rounded-full px-2.5 py-1 bg-[#E5A84B]/20 text-[#E5A84B]"><Loader2 size={12} className="animate-spin" /> Awaiting payment</span>}
                    {hpStatus === "timeout" && <span data-testid="hitpay-badge" className="text-[11px] font-semibold rounded-full px-2.5 py-1 bg-white/10 text-[#A19DB0]">Not confirmed yet</span>}
                    {hpStatus === "paid" && <span data-testid="hitpay-badge" className="text-[11px] font-semibold rounded-full px-2.5 py-1 bg-[#4ADE80]/15 text-[#4ADE80]">Paid ✓</span>}
                  </div>
                  <p className="text-xs text-[#A19DB0]">Your passes activate automatically once paid — no screenshot needed.</p>
                  {hpStatus === "cancelled" ? (
                    <div data-testid="hitpay-cancelled" className="rounded-xl bg-[#E57373]/10 border border-[#E57373]/30 p-3 text-center space-y-2">
                      <p className="text-sm text-[#ffb4b4]">Payment cancelled or not completed. Would you like to try again?</p>
                      <div className="flex gap-2">
                        <button onClick={() => startPayment(hpView)} data-testid="retry-yes" className="flex-1 rounded-full py-2 text-sm font-semibold bg-[#D4AF37] text-black active:scale-95 transition-transform">Yes, try again</button>
                        <button onClick={() => { setHpStatus("idle"); setHpQr(""); }} data-testid="retry-no" className="flex-1 rounded-full py-2 text-sm font-medium bg-white/10 text-[#E8E4F0]">No</button>
                      </div>
                    </div>
                  ) : hpQr ? (
                    <div className="space-y-2">
                      <div data-testid="hitpay-qr" className="rounded-xl bg-white p-4 flex flex-col items-center gap-2">
                        {hpQr.startsWith("http") ? <img src={hpQr} alt="PayNow QR" className="w-52 h-52 object-contain" /> : <QRCode value={hpQr} size={200} />}
                        <p className="text-black text-xs font-medium">Scan with your bank app to pay {money(order.total)}</p>
                      </div>
                      <button onClick={checkNow} data-testid="hitpay-check-now" className="w-full rounded-full py-2 text-sm font-medium bg-white/10 text-[#E8E4F0] hover:bg-white/20 transition-colors">I've paid — check now</button>
                    </div>
                  ) : (
                    <>
                      <button onClick={() => startPayment("checkout")} disabled={hpBusy || hpPolling} data-testid="pay-hitpay-card" className="w-full rounded-full py-3 font-semibold bg-[#D4AF37] text-black active:scale-95 transition-transform disabled:opacity-60">Pay {money(order.total)} by card</button>
                      {(hpPolling || hpStatus === "timeout") && (
                        <div className="space-y-2" data-testid="hitpay-waiting">
                          <p className="text-xs text-[#A19DB0] text-center">Complete payment in the other tab — this updates automatically once confirmed.</p>
                          <button onClick={checkNow} data-testid="hitpay-check-now" className="w-full rounded-full py-2 text-sm font-medium bg-white/10 text-[#E8E4F0] hover:bg-white/20 transition-colors">I've completed payment — check now</button>
                        </div>
                      )}
                    </>
                  )}
                  <button onClick={backToModes} data-testid="pay-back-card" className="w-full rounded-full py-2 text-sm text-[#A19DB0] hover:text-white flex items-center justify-center gap-1.5"><ArrowLeft size={14} /> Change payment method</button>
                </div>
              )}

              {payMode === "qr" && (methods.length === 0 ? (
                <p className="text-[#E57373] text-sm">No payment method configured yet. Please contact the studio.</p>
              ) : (
                <>
                  <button onClick={backToModes} data-testid="pay-back-qr" className="w-full rounded-full py-2 text-sm text-[#A19DB0] hover:text-white flex items-center justify-center gap-1.5 mb-1"><ArrowLeft size={14} /> Change payment method</button>
                  <div>
                    <p className="text-xs text-[#A19DB0] mb-2">PayNow / PayLah</p>
                    <div className="flex flex-wrap gap-2">
                      {methods.map((m) => (
                        <button key={m.id} onClick={() => setMethod(m)} data-testid={`method-${m.id}`} className={`rounded-full px-4 py-2 text-sm border transition-colors ${method?.id === m.id ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "bg-white/5 border-white/10 text-[#E8E4F0]"}`}>
                          {m.label}<span className="uppercase text-[10px] ml-1 opacity-70">{m.kind}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {method && (
                    <div className="rounded-2xl bg-white/5 border border-white/10 p-4 text-center" data-testid="method-detail">
                      {method.qr_image && <img src={resolveFileUrl(method.qr_image)} alt="Payment QR" className="w-48 h-48 object-contain mx-auto rounded-xl bg-white p-2" data-testid="method-qr" />}
                      {method.url && <a href={method.url} target="_blank" rel="noreferrer" data-testid="method-url" className="inline-flex items-center gap-1.5 text-[#D4AF37] text-sm mt-3 underline"><ExternalLink size={14} /> Open {method.label} link</a>}
                      {!method.qr_image && !method.url && <p className="text-[#A19DB0] text-sm">Scan {method.label} and transfer {money(order.total)}.</p>}
                    </div>
                  )}

                  <div className="rounded-2xl border border-[#D4AF37]/40 bg-[#D4AF37]/[0.07] p-4">
                    <p className="text-xs text-[#A19DB0]">Payment reference — add this in your bank's <span className="text-[#E8E4F0]">"Comments for recipient"</span> field so we can match your transfer.</p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="font-display text-2xl font-bold tracking-widest text-[#D4AF37]" data-testid="order-reference">{order.reference}</span>
                      <button onClick={copyRef} data-testid="copy-reference" className="flex items-center gap-1.5 bg-white/10 rounded-full px-3 py-1.5 text-xs hover:bg-white/20"><Copy size={13} /> Copy</button>
                    </div>
                  </div>

                  <div>
                    <ImageDropzone value={proofUrl} onUploaded={setProofUrl} endpoint="/upload" resultKey="url" label="Upload your payment screenshot" />
                  </div>

                  <button onClick={submitProof} disabled={busy || !proofUrl} data-testid="submit-proof" className="w-full rounded-full py-3.5 font-semibold bg-[#D4AF37] text-black active:scale-[0.98] transition-transform disabled:opacity-60">
                    {busy ? "Submitting…" : "I've paid — submit for approval"}
                  </button>
                </>
              ))}
            </div>
          )}

          {step === "done" && (
            <div className="text-center py-6 space-y-3" data-testid="checkout-step-done">
              <CheckCircle2 size={56} className={`mx-auto ${hpStatus === "paid" ? "text-[#4ADE80]" : "text-[#D4AF37]"}`} />
              {hpStatus === "paid" ? (
                <>
                  <p className="font-display text-2xl font-bold text-[#4ADE80]" data-testid="paid-badge">Paid ✓</p>
                  <p className="text-[#A19DB0] text-sm">Payment for <span className="text-[#D4AF37] font-semibold">{order?.reference}</span> is confirmed — your purchase is now active. Enjoy!</p>
                </>
              ) : (
                <>
                  <p className="text-[#A19DB0] text-sm">Your proof for <span className="text-[#D4AF37] font-semibold">{order?.reference}</span> is now pending verification. We'll notify you and add your pass{cart.length !== 1 ? "es" : ""} once it's approved.</p>
                  <div className="inline-flex items-center gap-1.5 text-xs text-[#A19DB0] bg-white/5 rounded-full px-3 py-1.5"><Clock size={13} /> Awaiting admin approval</div>
                </>
              )}
              <button onClick={() => { setCheckoutOpen(false); navigate("/"); }} data-testid="done-close" className="w-full rounded-full py-3.5 font-semibold bg-white/10 border border-white/10 mt-2">Back to home</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Screen>
  );
}

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between"><span className="text-[#A19DB0]">{label}</span><span>{value}</span></div>
);
